import React, { Fragment } from 'react'
import styled from 'styled-components'
import Button from '@mui/material/Button'
import CheckIcon from '@mui/icons-material/Check'

import { filterGroups, filterGroupsWithoutHEHM, filtersFriendlyNames } from 'pages/Customers/CustomerSearch/filterGroups'
import { Alignment, Category, Selection } from 'pages/Customers/CustomerSearch//enums'

import { friendlyName } from 'helpers/formatters'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'
import { useFeaturesContext } from 'hooks/FeaturesContext'

type Props = {
  group?: {
    category: string;
    alignItems: string;
    label: string;
    rule: Selection;
    filters: string[];
  } | null;
  selectedFilters: string[];
  toggleFilterCallback: (arg1: { rule: Selection, filters: string[] }, arg2: string, arg3: boolean) => void;
}

function FilterSection ({ group = null, selectedFilters, toggleFilterCallback }: Props) {
  if (!group) return null
  return (
    <S.FilterSection alignment={group.alignItems} key={group.label + '-' + group.filters[0]}>
      <S.Label>{friendlyName(group.label)}</S.Label>
      <S.Filters>
        {group.filters.map(filter => {
          const isSelected = selectedFilters.includes(filter)

          return (
            <S.FilterButton
              key={filter}
              disableRipple
              className={isSelected ? 'selected filter ' + filter : 'filter ' + filter}
              onClick={() => { toggleFilterCallback(group, filter, isSelected) }}
            >
              {isSelected && <S.CheckIcon />}
              {friendlyName(filter, filtersFriendlyNames)}
            </S.FilterButton>
          )
        })}
      </S.Filters>
    </S.FilterSection>
  )
}

const AdvancedFilters = ({ selectedFilters, toggleFilterCallback }: Props) => {
  const { enabledFeatures } = useFeaturesContext()

  return (
    <S.AdvancedFilters>
      {enabledFeatures.includes('hehm') &&
      <>
        <S.Group>
          <S.GroupHeaderText>HVAC Performance Monitoring Kit</S.GroupHeaderText>
          {filterGroups()
            .filter(group => group.category === Category.HEHM)
            .map(group => (
              <FilterSection key={group.label} {...{ group, selectedFilters, toggleFilterCallback }} />
            ))}
        </S.Group>

        <S.Group>
          <S.GroupHeaderText>Thermostat Only</S.GroupHeaderText>
          {filterGroups()
            .filter(group => group.category === Category.Thermostat)
            .map(group => (
              <FilterSection key={group.label} {...{ group, selectedFilters, toggleFilterCallback }} />
            ))}
        </S.Group>

        <S.LineBreak />
        <S.HeaderText>All</S.HeaderText>

        {filterGroups()
          .filter(group => group.category === Category.None)
          .map(group => (
            (group.alignItems === Alignment.LEFT || group.alignItems === Alignment.RIGHT) ? (
              <S.Group key={group.label}>
                <FilterSection {...{ group, selectedFilters, toggleFilterCallback }} />
              </S.Group>
            ) : group.alignItems === Alignment.CENTER ? (
              <S.CenterGroup key={group.label}>
                <FilterSection {...{ group, selectedFilters, toggleFilterCallback }} />
              </S.CenterGroup>
            ) : null
          ))}
      </>}

      {!enabledFeatures.includes('hehm') &&
      <>
        <S.Group>
          {filterGroupsWithoutHEHM().map(group => (
            <div key={group.label}>{group.alignItems === Alignment.LEFT && <FilterSection {...{ group, selectedFilters, toggleFilterCallback }} />}</div>
          ))}
        </S.Group>
        <S.Group>
          {filterGroupsWithoutHEHM().map(group => (
            <div key={group.label}>{group.alignItems === Alignment.RIGHT && <FilterSection {...{ group, selectedFilters, toggleFilterCallback }} />}</div>
          ))}
        </S.Group>
      </>}
    </S.AdvancedFilters>
  )
}

export default AdvancedFilters

const S = {
  CheckIcon: styled(CheckIcon)`
    font-size: 20px;
    margin-right: 5px;
    margin-left: -5px;
  `,
  FilterButton: styled(Button)`
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: ${color('customerFilterText')};
    border: 1px solid ${color('customerFilterBorder')};
    border-radius: 25px;
    cursor: pointer;
    transition: background-color 0.3s, color 0.3s;
    margin: 5px;
    text-transform: capitalize;
    padding: 5px 10px;

    @media ${screenSize.small} {
      margin: 3px;
      font-size: 11px;
      padding: 4px 8px;
    }

    &:hover, &:focus {
      border: 1px solid ${color('customerFilterHoverBorder')} !important;
      background-color: ${color('customerFilterHoverBackground')};
    }

    &.selected{
      font-weight: 800;
      color: ${color('customerFilterSelected')};
      background-color: ${color('customerFilterSelectedBackground')};
      border: 1px solid ${color('customerFilterSelectedBorder')} ;
    }
  `,
  FilterSection: styled.div.withConfig({
    shouldForwardProp: (prop) => !['alignment'].includes(prop)
  })<{ alignment: string }>`
    display: flex;
    flex-direction: column;
    background-color: ${color('customerFilterGroup')};
    border: 1px solid ${color('customerFilterGroup')};
    border-radius: 15px;
    margin: 5px;
    padding-top: 5px;
    font-size: 12px;

    ${({ alignment }) => alignment === Alignment.LEFT ? 'margin-left: 18px'
    : alignment === Alignment.RIGHT ? 'margin-right: 18px' : 'margin: 5px 18px'};

    @media ${screenSize.small} {
      margin: 5px 10px !important;
    }
  `,
  Label: styled.div`
    color: ${color('customerFilterText')};
    padding-left: 15px;
    text-align: justify;
    width: 100px;

    @media ${screenSize.small} {
      width: auto;
      max-width: 100px;
    }
  `,
  Filters: styled.div`
    display: flex;
    flex-wrap: wrap;
    white-space: normal;
    margin: 5px 10px;

    @media ${screenSize.small} {
      margin: 5px 5px;
    }
  `,
  AdvancedFilters: styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    @media ${screenSize.small} {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
  `,
  Group: styled.div`
    display: flex;
    flex-direction: column;

    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  CenterGroup: styled.div`
    display: flex;
    flex-direction: column;
    grid-column: span 2;
  `,
  GroupHeaderText: styled.span`
    padding: 10px 0px 0px 20px;
    font-weight: 700;
  `,
  HeaderText: styled.span`
    padding-left: 20px;
    font-weight: 700;
    grid-column: span 2;
  `,
  LineBreak: styled.div`
    border-bottom: 1px solid ${color('trendHeaderBackground')};
    grid-column: span 2;
    margin: 5px 30px;
  `
}
